import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { DsButtonVariants, DsModule } from '@levelaccess/design-system';
import { CommonModule } from '@angular/common';
import { SharedCommonUtility } from '@shared/utils/common.utility';

import { SharedModule } from '../../../../shared/shared.module';
import { PortfolioSharedModule } from '../../../../pages/public/portfolio/portfolio-shared.module';

import { IDigitalProperty, IQuickMonitoringSetupRequest } from '../../../../../../shared/interfaces/digital-property.interface';
import { IComponentFormState } from '../../../../interfaces/advanced-scan-form-state.interface';
import { Api } from '../../../../../../shared/constants/api';
import { $workspace, CHAR_LIMITS } from '../../../../../../shared/constants/workspace';
import { CustomValidators } from '../../../../services/helpers/form-custom-validators';
import { NotificationPosition } from '../../../../models/notification.model';
import { NotificationService } from '../../../../services/notification.service';
import { LoadErrorHandler } from '../../../../services/load-error-handler';
import { TranslateService } from '../../../../translate/translate.service';
import { WorkspaceService } from '../../../../services/workspace.service';
import { $digitalProperty, $digitalPropertyMonitoring } from '../../../../../../shared/constants/digital-properties';
import { SharedTextUtility } from '../../../../../../shared/utils/text.utility';
import { AuditToolCommonNames } from '../../../../../../shared/constants/audit-tool';
import { DigitalPropertyFormDetailsComponent } from '../../../../pages/public/portfolio/digital-property-form-details/digital-property-form-details.component';
import { MonitoringQuickSetupComponent } from '../../../../components/digital-property-monitoring/quick-setup/monitoring-quick-setup.component';
import { ErrorMessageService } from '../../../../services/error-message.service';
import { TenantRouteUtil } from '../../../../admin/tenants/tenant-route.util';
import { CommonUtility } from '../../../../utility/common.utility';
import { errorMessagesNames } from '../../../../../../shared/constants/errors';
import { FeatureFlagCollection } from '../../../../../../shared/interfaces/feature-flag.interface';
import { FeatureFlagService } from '../../../../services/feature-flag/feature-flag.service';
import { IWorkspace } from '../../../../../../shared/interfaces/workspace.interface';
import { BusMessageChannels, BusMessageService } from '../../../../services/bus-message.service';

enum WorkspaceForm {
  workspaceName = 'workspaceName',
  workspaceDescription = 'workspaceDescription',
}

enum DigitalPropertyForm {
  propertyDetails = 'propertyDetails',
}

interface CreateWorkspacePlusDpFormState {
  [WorkspaceForm.workspaceName]: string;
  [WorkspaceForm.workspaceDescription]: string;
  [DigitalPropertyForm.propertyDetails]: IDigitalProperty;
}

@Component({
  selector: 'app-create-tenant-workspace-plus-dp',
  templateUrl: './create-tenant-workspace-plus-dp.component.html',
  imports: [CommonModule, ReactiveFormsModule, RouterModule, DsModule, SharedModule, PortfolioSharedModule],
  standalone: true,
})
export class CreateTenantWorkspacePlusDpComponent implements OnInit {
  protected Api: typeof Api;
  protected $workspace: typeof $workspace;
  protected CHAR_LIMITS: Readonly<Partial<Record<$workspace, number>>>;
  public WorkspaceForm: typeof WorkspaceForm;
  public tenantDetailsUrl: string[];

  protected monitoringEnabled: boolean;
  protected formValidationRequest$: Subject<void>;
  public form: UntypedFormGroup;
  protected bulkWorkspaceAndDpCreation: boolean;

  protected readonly DsButtonVariants: typeof DsButtonVariants = DsButtonVariants;
  private subscriptions: Subscription;
  @ViewChild(DigitalPropertyFormDetailsComponent) private formDetailsComponent: DigitalPropertyFormDetailsComponent;
  @ViewChild(MonitoringQuickSetupComponent) private quickMonitoringSettings: MonitoringQuickSetupComponent;
  @Input() modalMode: boolean = false;
  @Input() singleWorkspaceOnly: boolean = false;
  @Input() tenantId?: string;

  @Output() workspaceCreated: EventEmitter<IWorkspace> = new EventEmitter<IWorkspace>();
  @Output() cancelled: EventEmitter<void> = new EventEmitter<void>();

  public constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private formBuilder: UntypedFormBuilder,
    private changeDetectorRef: ChangeDetectorRef,
    private notificationService: NotificationService,
    private loadErrorHandler: LoadErrorHandler,
    private translateService: TranslateService,
    private workspaceService: WorkspaceService,
    private element: ElementRef<HTMLElement>,
    private errorMessageService: ErrorMessageService,
    private featureFlagService: FeatureFlagService,
    private busMessageService: BusMessageService,
  ) {
    this.subscriptions = new Subscription();
    this.Api = Api;
    this.$workspace = $workspace;
    this.WorkspaceForm = WorkspaceForm;
    this.CHAR_LIMITS = CHAR_LIMITS;
    this.formValidationRequest$ = new Subject<void>();
  }

  public get isWebsite(): boolean {
    return this.formDetailsComponent?.isWebsite ?? false;
  }

  private get defaultNoDomainRestrictions(): boolean {
    return this.isWebsite === false;
  }

  public get url(): string | undefined {
    return this.form.get(DigitalPropertyForm.propertyDetails)?.get($digitalProperty.url)?.value;
  }

  public get toolName(): AuditToolCommonNames | undefined {
    return this.form.get(DigitalPropertyForm.propertyDetails)?.get($digitalProperty.defaultTool)?.value;
  }

  public websiteDetailsChanged(data: IComponentFormState<IDigitalProperty>): void {
    this.form.setControl(DigitalPropertyForm.propertyDetails, data.form);
    this.changeDetectorRef.detectChanges();
  }

  public onSubmit(): void {
    this.formValidationRequest$.next();

    if (this.form.valid === false) {
      this.errorMessageService.setFocusOnFirstError(this.element.nativeElement);
      return;
    }

    if (this.isWebsite && this.monitoringEnabled && !this.quickMonitoringSettings?.isValid()) {
      return;
    }

    const formValue: CreateWorkspacePlusDpFormState = this.form.getRawValue();
    const dpDetails: IDigitalProperty = formValue[DigitalPropertyForm.propertyDetails];

    let monitoring: IQuickMonitoringSetupRequest;
    if (this.isWebsite && this.monitoringEnabled) {
      monitoring = this.quickMonitoringSettings.getData();
    }

    this.subscriptions.add(
      this.workspaceService
        .createWorkspacePlusDp({
          [Api.workspace]: {
            [$workspace.name]: formValue[WorkspaceForm.workspaceName],
            [$workspace.description]: formValue[WorkspaceForm.workspaceDescription],
            [$workspace.tenantId]: this.tenantId,
          },
          [Api.digital_property]: {
            [$digitalProperty.name]: dpDetails.name,
            [$digitalProperty.propertyKey]: dpDetails.propertyKey,
            [$digitalProperty.typeId]: dpDetails.typeId,
            [$digitalProperty.defaultTool]: dpDetails.defaultTool,
            [$digitalProperty.url]: dpDetails.url,
            [$digitalProperty.noDomainRestrictions]: dpDetails.noDomainRestrictions || this.defaultNoDomainRestrictions,
            [$digitalProperty.authorizedDomains]: SharedTextUtility.getLinesFromText(
              dpDetails[$digitalProperty.authorizedDomains]?.toString(),
            ),
            [$digitalProperty.privacyObfuscationEnabled]: dpDetails.privacyObfuscationEnabled,
            ...(SharedCommonUtility.notNullish(dpDetails[$digitalProperty.mobileBundleId])
              ? { [$digitalProperty.mobileBundleId]: dpDetails[$digitalProperty.mobileBundleId] }
              : {}),
          },
          [Api.monitoring]: monitoring,
        })
        .subscribe({
          next: async (response: IWorkspace): Promise<void> => {
            this.notificationService.success(
              this.translateService.instant('workspace_creation_success'),
              NotificationPosition.Toast,
              true,
            );

            this.busMessageService.to(BusMessageChannels.workspaceCreated).next();

            if (this.modalMode) {
              this.workspaceCreated.emit(response);
            } else {
              await this.router.navigate(this.tenantDetailsUrl);
            }
          },
          error: (error: HttpErrorResponse): void => {
            if (CommonUtility.extractHTTPErrorName(error) === errorMessagesNames.DigitalPropertyUsageLimitReached) {
              this.loadErrorHandler.handleError(
                this.translateService.instant('digital_property_usage_limit_reached'),
                error,
                CreateTenantWorkspacePlusDpComponent.name,
              );
            } else if (
              CommonUtility.extractHTTPErrorName(error) === errorMessagesNames.DigitalPropertyMonitoringInvalidStartDate
            ) {
              this.loadErrorHandler.handleError(
                this.translateService.instant(
                  'monitoring_start_date_less_than_one_year',
                  monitoring[$digitalPropertyMonitoring.startAt],
                ),
                error,
                CreateTenantWorkspacePlusDpComponent.name,
              );
            } else {
              this.loadErrorHandler.handleError(
                this.translateService.instant('workspace_creation_error'),
                error,
                CreateTenantWorkspacePlusDpComponent.name,
              );
            }
          },
        }),
    );
  }

  public onCancel(): void {
    if (this.modalMode) {
      this.cancelled.emit();
    } else {
      this.router.navigate(this.tenantDetailsUrl);
    }
  }

  public ngOnInit(): void {
    const inputTenantId = this.tenantId;
    const routeTenantId = this.activatedRoute.snapshot.params[Api.tenantId];
    this.tenantId = inputTenantId || routeTenantId;

    this.form = this.formBuilder.group({
      [WorkspaceForm.workspaceName]: this.formBuilder.control(
        '',
        [CustomValidators.required, Validators.maxLength(CHAR_LIMITS[$workspace.name])],
        [this.workspaceService.validateNameUniqueness(this.tenantId).bind(this)],
      ),
      [WorkspaceForm.workspaceDescription]: this.formBuilder.control('', [
        CustomValidators.required,
        Validators.maxLength(CHAR_LIMITS[$workspace.description]),
      ]),
      [DigitalPropertyForm.propertyDetails]: {},
    });

    this.tenantDetailsUrl = [TenantRouteUtil.getTenantRoutePath(this.router.url), this.tenantId];

    if (this.singleWorkspaceOnly) {
      this.bulkWorkspaceAndDpCreation = false;
    } else {
      this.subscriptions.add(
        this.featureFlagService
          .variation$(FeatureFlagCollection.bulkWsAndDpsCreationWithCustomMonitoringSettings, false)
          .pipe(map(Boolean))
          .subscribe({
            next: (value: boolean): void => {
              this.bulkWorkspaceAndDpCreation = value;
            },
            error: (): void => {
              this.bulkWorkspaceAndDpCreation = false;
            },
          }),
      );
    }
  }
}
